import axios from 'axios';
import config from '../config';
import { SystemSettings } from '../types/system';
import { Course, CourseItem } from '../types/course';
import { BusinessHour, BusinessHourResponse } from '../types/businessHour';
import authService from './authService';
import api from './apiClient';

interface NotificationSettings {
  email: boolean;
  slack: boolean;
  line: boolean;
  emailSettings?: {
    host: string;
    port: number;
    username: string;
    password: string;
  };
  slackSettings?: {
    webhookUrl: string;
    channel: string;
  };
  lineSettings?: {
    channelId: string;
    channelSecret: string;
    accessToken: string;
  };
}

interface LineSettings {
  channelId: string;
  channelSecret: string;
  accessToken: string;
  [key: string]: any;
}

interface BackupSettings {
  autoBackup: boolean;
  backupInterval: number;
  maxBackups: number;
  [key: string]: any;
}

// APIベースURL
const API_BASE_URL = config.api.baseURL || '';

// システム設定のキャッシュ
let systemSettingsCache: SystemSettings | null = null;
let systemSettingsCacheTimestamp: number = 0;

// 営業時間データのキャッシュ
let businessHoursCache: BusinessHourResponse | BusinessHour[] | null = null;
let businessHoursCacheTimestamp: number = 0;
const CACHE_TTL = 60000; // キャッシュの有効期間（ミリ秒）: 1分

// 認証ヘッダーを取得する関数
const getAuthHeader = () => {
  return authService.getAuthHeader();
};

// APIリクエストを送信する関数
const sendRequest = async (url: string, options: any = {}) => {
  try {
    // 認証ヘッダーを取得
    const authHeader = getAuthHeader();
    let headers = options.headers || {};
    
    // ヘッダーをマージ
    headers = {
      ...headers,
      ...authHeader,
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };

    const config = {
      ...options,
      headers,
      withCredentials: true
    };

    console.log('APIリクエスト設定:', {
      url,
      method: options.method || 'GET',
      hasToken: !!authHeader.Authorization
    });

    const response = await axios(url, config);
    return response.data;
  } catch (error: any) {
    console.error('APIリクエストエラー:', {
      url,
      method: options.method || 'GET',
      error: error.message,
      response: error.response?.data
    });
    throw error;
  }
};

// モックデータを提供するヘルパー関数
const provideMockData = (type: string): any => {
  console.log(`${type}の取得に失敗 - モックデータを返します`);
  
  // 各設定のデフォルト値
  const defaults: Record<string, any> = {
    'システム設定': {
      notification_enabled: false,
      language: 'ja',
      map_default_zoom: 12,
      gps_tracking_interval: 60,
      line_notification_enabled: false,
      automated_report: false
    },
    '通知設定': {
      email: false,
      slack: false,
      line: false,
      emailSettings: {
        host: '',
        port: 587,
        username: '',
        password: ''
      },
      slackSettings: {
        webhookUrl: '',
        channel: 'general'
      },
      lineSettings: {
        channelId: '',
        channelSecret: '',
        accessToken: ''
      }
    },
    'LINE設定': {
      channelId: '',
      channelSecret: '',
      accessToken: '',
      enabled: false
    },
    'バックアップ設定': {
      autoBackup: false,
      backupInterval: 24,
      maxBackups: 7,
      lastBackup: null
    },
    'コース一覧': [],
    '営業時間': [
      { day_of_week: 0, start_time: '09:00', end_time: '18:00', is_business_day: true },
      { day_of_week: 1, start_time: '09:00', end_time: '18:00', is_business_day: true },
      { day_of_week: 2, start_time: '09:00', end_time: '18:00', is_business_day: true },
      { day_of_week: 3, start_time: '09:00', end_time: '18:00', is_business_day: true },
      { day_of_week: 4, start_time: '09:00', end_time: '18:00', is_business_day: true },
      { day_of_week: 5, start_time: '10:00', end_time: '15:00', is_business_day: false },
      { day_of_week: 6, start_time: '10:00', end_time: '15:00', is_business_day: false }
    ]
  };
  
  return defaults[type] || {};
};

/**
 * 営業時間データを取得する
 * @returns 営業時間のレスポンス
 */
export const getBusinessHours = async (): Promise<BusinessHourResponse | BusinessHour[]> => {
  try {
    // キャッシュチェック
    const now = Date.now();
    if (businessHoursCache && (now - businessHoursCacheTimestamp < CACHE_TTL)) {
      console.log('キャッシュから営業時間データを返します（有効期限内）');
      return businessHoursCache;
    }
    
    console.log('営業時間データを取得します...');
    
    // 共通のAPIクライアントを使用
    try {
      const response = await api.get('/system/business-hours', {
        timeout: 15000, // タイムアウト延長
        headers: {
          'Cache-Control': 'no-cache'
        }
      });
      
      console.log('営業時間データレスポンス:', response.data);
      
      // レスポンスをキャッシュ
      businessHoursCache = response.data;
      businessHoursCacheTimestamp = Date.now();
      
      return response.data;
    } catch (error: any) {
      console.error('営業時間データの取得エラー:', error);
      
      // 429エラーの場合は古いキャッシュを使用
      if (error?.response?.status === 429) {
        console.warn('営業時間取得 - レート制限エラー、古いキャッシュを使用');
        if (businessHoursCache) {
          return businessHoursCache;
        }
      }
      
      throw error;
    }
  } catch (error) {
    console.error('営業時間データの取得に失敗しました:', error);
    
    // 過去にキャッシュしたデータがあれば期限切れでも使用
    if (businessHoursCache) {
      console.log('エラー発生のため期限切れのキャッシュデータを使用します');
      return businessHoursCache;
    }
    
    // モックデータを返す
    const mockBusinessHours = provideMockData('営業時間');
    return {
      success: true,
      data: mockBusinessHours
    };
  }
};

/**
 * システム設定を取得する
 * @returns システム設定オブジェクト
 */
export const getSystemSettings = async (): Promise<SystemSettings> => {
  try {
    // console.log("設定を取得中...");
    const response = await api.get('/system/settings', {
      headers: {
        Authorization: `Bearer ${authService.getToken()}`
      }
    });
    
    console.log('システム設定データレスポンス:', response.data);
    
    // レスポンスの構造を確認
    if (response.data && response.data.success && response.data.data) {
      return response.data.data;
    }
    
    return response.data;
  } catch (error: any) {
    console.error('システム設定の取得エラー:', error);
    
    // 429エラーの場合は即座にモックデータを返す
    if (error?.response?.status === 429) {
      console.warn('システム設定取得 - レート制限エラー、モックデータを返します');
      return provideMockData('システム設定');
    }
    
    throw error;
  }
};

/**
 * 通知設定を取得する
 * @returns 通知設定オブジェクト
 */
export const getNotificationSettings = async (): Promise<NotificationSettings> => {
  try {
    console.log('通知設定を取得します...');
    
    const response = await api.get('/system/settings/notifications');
    console.log('通知設定レスポンス:', response.data);
    return response.data;
  } catch (error) {
    console.error('通知設定の取得に失敗しました:', error);
    return provideMockData('通知設定');
  }
};

/**
 * LINE設定を取得する
 * @returns LINE設定オブジェクト
 */
export const getLineSettings = async (): Promise<LineSettings> => {
  try {
    console.log('LINE設定を取得します...');
    
    const response = await api.get('/system/settings/line');
    console.log('LINE設定レスポンス:', response.data);
    return response.data;
  } catch (error) {
    console.error('LINE設定の取得に失敗しました:', error);
    return provideMockData('LINE設定');
  }
};

/**
 * バックアップ設定を取得する
 * @returns バックアップ設定オブジェクト
 */
export const getBackupSettings = async (): Promise<BackupSettings> => {
  try {
    console.log('バックアップ設定を取得します...');
    
    const response = await api.get('/system/settings/backup');
    console.log('バックアップ設定レスポンス:', response.data);
    return response.data;
  } catch (error) {
    console.error('バックアップ設定の取得に失敗しました:', error);
    return provideMockData('バックアップ設定');
  }
};

/**
 * コース一覧を取得する
 * @returns コース一覧
 */
export const getCourses = async (): Promise<Course[]> => {
  try {
    const response = await api.get('/system/courses');
    if (response.data && Array.isArray(response.data.courses)) {
      return response.data.courses;
    }
    if (response.data && Array.isArray(response.data)) {
      return response.data;
    }
    console.warn('コースデータの形式が不正です:', response.data);
    return [];
  } catch (error) {
    console.error('コース一覧の取得に失敗しました:', error);
    throw error;
  }
};

/**
 * コースの更新
 * @param id コースID
 * @param courseData 更新するコースデータ
 */
export const updateCourse = async (id: string, courseData: Omit<CourseItem, 'id'>): Promise<CourseItem> => {
  try {
    console.log(`コースID=${id}を更新します:`, courseData);
    const response = await api.put(`/system/settings/courses/${id}`, courseData);
    console.log('コース更新レスポンス:', response.data);
    return response.data;
  } catch (error) {
    console.error(`コースID=${id}の更新に失敗しました:`, error);
    throw error;
  }
};

/**
 * コースの削除
 * @param id 削除するコースID
 */
export const deleteCourse = async (id: string): Promise<void> => {
  try {
    console.log(`コースID=${id}を削除します`);
    const response = await api.delete(`/system/settings/courses/${id}`);
    console.log(`コースID=${id}削除レスポンス:`, response.data);
    
    if (response.status >= 200 && response.status < 300) {
      // 削除成功、バックエンド処理のために少し待機
      await new Promise(resolve => setTimeout(resolve, 500));
      console.log(`コースID=${id}の削除に成功しました`);
    } else {
      console.error(`コースID=${id}の削除に失敗: ステータス ${response.status}`);
    }
  } catch (error) {
    console.error(`コースID=${id}の削除に失敗:`, error);
    throw error;
  }
};

export const updateSystemSettings = async (settings: Partial<SystemSettings>): Promise<SystemSettings> => {
  try {
    const response = await api.put('/system/settings', settings);
    // console.log("更新された設定:", response.data);
    return response.data;
  } catch (error) {
    console.error('システム設定の更新に失敗しました:', error);
    throw error;
  }
};

/**
 * ダッシュボードの連絡事項（リッチテキストHTML）を更新する。
 * バックエンド側で管理者系(admin/manager)のみ許可される。
 */
export const updateDashboardNotice = async (html: string): Promise<string> => {
  const response = await api.put('/system/notice', { dashboard_notice: html });
  return response.data?.data?.dashboard_notice ?? html;
};

export const updateNotificationSettings = async (settings: NotificationSettings): Promise<NotificationSettings> => {
  try {
    const response = await api.put('/system/settings/notifications', settings);
    return response.data;
  } catch (error) {
    console.error('通知設定の更新に失敗しました:', error);
    return settings; // エラー時は入力された設定をそのまま返す
  }
};

export const updateLineSettings = async (settings: LineSettings): Promise<LineSettings> => {
  try {
    const response = await api.put('/system/settings/line', settings);
    return response.data;
  } catch (error) {
    console.error('LINE設定の更新に失敗しました:', error);
    return settings; // エラー時は入力された設定をそのまま返す
  }
};

export const testLineConnection = async (): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.post('/system/settings/line/test');
    return response.data;
  } catch (error) {
    console.error('LINE接続テストに失敗しました:', error);
    return { success: false, message: 'LINE接続テストに失敗しました' };
  }
};

export const updateBackupSettings = async (settings: BackupSettings): Promise<BackupSettings> => {
  try {
    const response = await api.put('/system/settings/backup', settings);
    return response.data;
  } catch (error) {
    console.error('バックアップ設定の更新に失敗しました:', error);
    return settings; // エラー時は入力された設定をそのまま返す
  }
};

export const createBackup = async (): Promise<{ id: string; message: string }> => {
  try {
    const response = await api.post('/system/settings/backup/create');
    return response.data;
  } catch (error) {
    console.error('バックアップの作成に失敗しました:', error);
    return { id: '', message: 'バックアップの作成に失敗しました' };
  }
};

export const deleteBackup = async (backupId: number): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.delete(`/system/settings/backup/${backupId}`);
    return response.data;
  } catch (error) {
    console.error('バックアップの削除に失敗しました:', error);
    return { success: false, message: 'バックアップの削除に失敗しました' };
  }
};

export const restoreBackup = async (backupId: string): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.post(`/system-settings/backup/restore/${backupId}`);
    return response.data;
  } catch (error) {
    console.error('バックアップ復元に失敗しました:', error);
    return { success: false, message: 'バックアップ復元に失敗しました' };
  }
};

/**
 * 複数のコースを一括で削除します
 * @param ids 削除するコースのID配列
 * @param physicalDelete 物理削除を行うかどうかのフラグ
 * @returns 削除に成功したコースIDの配列
 */
export const deleteMultipleCourses = async (ids: string[], physicalDelete: boolean = false): Promise<string[]> => {
  if (!ids || ids.length === 0) {
    console.log('削除対象のコースIDが指定されていません');
    return [];
  }
  
  console.log(`${ids.length}件のコースを一括削除します: ${ids.join(', ')}`);
  if (physicalDelete) {
    console.log('物理削除モードが有効です');
  }
  
  try {
    // バックエンドの一括削除エンドポイントを試す（実装されている場合）
    try {
      // IDを数値に変換してバッチ削除リクエストを送信
      const numericIds = ids.map(id => parseInt(id, 10));
      console.log('数値変換後のID配列:', numericIds);
      
      // リクエスト形式をバックエンドの期待する形式に合わせる
      // 1. JSON形式で送信するパターン
      const batchResponse = await api.delete('/system/settings/courses/batch', { 
        data: { ids: numericIds, physicalDelete } 
      });
      
      console.log('コース一括削除API応答:', batchResponse.data);
      
      if (batchResponse.status >= 200 && batchResponse.status < 300) {
        console.log(`${ids.length}件のコース一括削除に成功しました`);
        
        // データベース更新を確実にするため待機
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        return ids;
      }
    } catch (batchError) {
      console.log('一括削除エンドポイントが存在しないか、エラーが発生しました。個別削除に切り替えます。', batchError);
    }
    
    // 個別削除処理
    const successfulIds: string[] = [];
    const failedIds: string[] = [];
    
    // 各コースを順次削除
    for (const id of ids) {
      try {
        console.log(`個別削除処理中: ID=${id}`);
        const response = await api.delete(`/system/settings/courses/${id}`, {
          params: { physicalDelete }
        });
        
        console.log(`コース削除API応答: ID=${id}`, response.data);
        
        if (response.status >= 200 && response.status < 300) {
          successfulIds.push(id);
          console.log(`ID=${id}の削除成功`);
          
          // バックエンド処理のために少し待機
          await new Promise(resolve => setTimeout(resolve, 500));
        } else {
          console.error(`ID=${id}の削除に失敗: ステータス ${response.status}`);
          failedIds.push(id);
        }
      } catch (error) {
        console.error(`ID=${id}の削除に失敗:`, error);
        failedIds.push(id);
      }
    }
    
    if (failedIds.length > 0) {
      console.warn(`${failedIds.length}件のコースが削除できませんでした: ${failedIds.join(', ')}`);
    }
    
    // 削除後、データベース更新を確実にするため待機
    if (successfulIds.length > 0) {
      console.log(`${successfulIds.length}件のコース削除後、データベース更新を待機中...`);
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    
    return successfulIds;
  } catch (error) {
    console.error('コース一括削除中にエラーが発生しました:', error);
    throw error;
  }
};

export const updateBusinessHour = async (
  dayOfWeek: number | null, 
  data: { 
    day_of_week?: string | number; 
    start_time: string; 
    end_time: string; 
    is_business_day: boolean 
  }
): Promise<BusinessHour> => {
  try {
    console.log(`営業時間更新リクエスト(曜日: ${dayOfWeek || data.day_of_week})を送信:`, data);
    const path = dayOfWeek !== null 
      ? `/system/settings/business-hours/${dayOfWeek}`
      : `/system/settings/business-hours/${data.day_of_week}`;
      
    const response = await api.put(path, {
      start_time: data.start_time,
      end_time: data.end_time,
      is_business_day: data.is_business_day
    });
    
    console.log(`営業時間更新レスポンス(曜日: ${dayOfWeek || data.day_of_week}):`, response.data);
    
    // レスポンスデータの検証
    if (!response.data || (response.data.success === false)) {
      console.error(`営業時間(曜日: ${dayOfWeek || data.day_of_week})の更新に失敗しました:`, response.data);
      throw new Error(response.data?.message || '営業時間の更新に失敗しました');
    }
    
    // レスポンスデータの構造に基づいて返却データを決定
    if (response.data.data) {
      return response.data.data;
    } else if (response.data.business_hour) {
      return response.data.business_hour;
    } else if (response.data.success === true) {
      // 成功フラグがあるが具体的なデータがない場合、送信データを返却
      let dayNum = 0; // デフォルト値
      
      if (typeof dayOfWeek === 'number') {
        dayNum = dayOfWeek;
      } else if (data.day_of_week !== undefined) {
        if (typeof data.day_of_week === 'number') {
          dayNum = data.day_of_week;
        } else if (typeof data.day_of_week === 'string') {
          // 数値に変換を試みる
          if (!isNaN(Number(data.day_of_week))) {
            dayNum = Number(data.day_of_week); // 文字列を数値に変換
          } else {
            // 曜日名からインデックスを取得
            const dayIndex = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].indexOf(data.day_of_week.toLowerCase());
            if (dayIndex !== -1) {
              dayNum = dayIndex;
            }
          }
        }
      }
      
      // 確実に数値型に変換
      const dayNumAsNumber = Number(dayNum);
      
      // 明示的に返却オブジェクトを作成
      return {
        day_of_week: dayNumAsNumber,
        start_time: data.start_time,
        end_time: data.end_time,
        is_business_day: data.is_business_day,
        id: dayNumAsNumber + 1, // 仮のID
        day_name: ['月曜日', '火曜日', '水曜日', '木曜日', '金曜日', '土曜日', '日曜日'][dayNumAsNumber] || ''
      };
    }
    
    // それ以外の場合
    return response.data;
  } catch (error) {
    console.error(`営業時間(曜日: ${dayOfWeek || data.day_of_week})の更新に失敗しました:`, error);
    if (axios.isAxiosError(error) && error.response) {
      console.error('エラーレスポンス:', error.response.data);
      throw new Error(error.response.data?.message || `営業時間(曜日: ${dayOfWeek || data.day_of_week})の更新に失敗しました`);
    }
    throw error;
  }
};

export const resetBusinessHours = async (): Promise<void> => {
  try {
    await api.post('/system/settings/business-hours/reset');
  } catch (error) {
    console.error('営業時間のリセットに失敗しました:', error);
    throw error;
  }
};

/**
 * 新しいコースを作成する
 * @param courseData 新しいコースデータ
 * @returns 作成されたコース情報
 */
export const createCourse = async (courseData: Omit<CourseItem, 'id'> & { id?: string }): Promise<CourseItem> => {
  try {
    console.log('新しいコースを作成します:', courseData);
    const response = await api.post('/system/settings/courses', courseData);
    console.log('コース作成レスポンス:', response.data);
    return response.data;
  } catch (error) {
    console.error('コースの作成に失敗しました:', error);
    throw error;
  }
};
